import { Compass, ArrowLeft, Info } from "lucide-react";

export const DOC_VERSION = "1.0 (juli 2026)";

export const KORTE_DISCLAIMER =
  "Zoetwaren Kompas kan fouten maken. Controleer belangrijke informatie altijd aan de hand van de cao, wetgeving, het functiehandboek of andere officiële bronnen.";

// Placeholder voor de toekomstige juridische entiteit (bewust zichtbaar als invulplek).
const ENTITEIT = "[juridische entiteit — nader te bepalen]";
const CONTACT = "[contact-e-mailadres — nader te bepalen]";
const VESTIGING = "[vestigingsplaats — nader te bepalen]";

const H2 = ({ children }) => (
  <h2 className="font-heading text-lg font-bold text-slate-900 mt-8 mb-2">{children}</h2>
);

const LegalShell = ({ title, testId, children }) => (
  <div className="min-h-screen bg-[#FAF6EE]">
    <header className="border-b border-[#EBE1D2] bg-white">
      <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-2">
        <span className="w-8 h-8 rounded-lg zk-brand-gradient flex items-center justify-center">
          <Compass className="w-4 h-4 text-white" />
        </span>
        <span className="font-heading font-bold text-[#3A281B]">Zoetwaren Kompas</span>
      </div>
    </header>
    <main data-testid={testId} className="max-w-3xl mx-auto px-5 py-8">
      <a href="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Terug
      </a>
      <h1 className="font-heading text-3xl font-bold text-slate-900 mb-1">{title}</h1>
      <p className="text-sm text-slate-500 mb-6" data-testid="doc-versie">Versie {DOC_VERSION}</p>
      <div className="text-[15px] leading-relaxed text-slate-700 space-y-3">{children}</div>
      <div className="mt-10 border-t border-[#EBE1D2] pt-5 text-sm text-slate-500" data-testid="doc-korte-disclaimer">
        {KORTE_DISCLAIMER}
      </div>
      <nav className="mt-4 text-sm text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
        <a href="/over" className="underline hover:text-slate-900">Over Zoetwaren Kompas</a>
        <a href="/voorwaarden" className="underline hover:text-slate-900">Algemene Voorwaarden</a>
        <a href="/privacy" className="underline hover:text-slate-900">Privacyverklaring</a>
      </nav>
    </main>
  </div>
);

export const Voorwaarden = () => (
  <LegalShell title="Algemene Voorwaarden" testId="doc-voorwaarden">
    <p>
      Deze Algemene Voorwaarden zijn van toepassing op het gebruik van Zoetwaren Kompas
      ("de applicatie"). Door akkoord te gaan bij het inloggen aanvaard je deze voorwaarden.
      Zoetwaren Kompas wordt aangeboden door {ENTITEIT}, gevestigd te {VESTIGING}.
    </p>

    <H2>Artikel 1 — Doel en aard van de applicatie</H2>
    <p>
      Zoetwaren Kompas is een kleinschalige testomgeving die wordt gebruikt om de werking,
      bruikbaarheid en kwaliteit van het systeem te evalueren. De applicatie is een informatief
      hulpmiddel voor werknemers en OR-leden in de zoetwarensector en beantwoordt vragen op
      basis van onder meer de Cao Zoetwaren, wetgeving en het functiehandboek.
    </p>

    <H2>Artikel 2 — Gebruik van kunstmatige intelligentie (AI)</H2>
    <p>
      De applicatie maakt gebruik van kunstmatige intelligentie (AI). Antwoorden worden
      automatisch gegenereerd op basis van beschikbare brondocumenten. AI-systemen kunnen
      fouten maken: antwoorden kunnen onjuist, onvolledig of verouderd zijn, ook wanneer zij
      overtuigend zijn geformuleerd.
    </p>

    <H2>Artikel 3 — Test- en ontwikkelfase</H2>
    <p>
      Het systeem bevindt zich in een test- en ontwikkelfase. Functionaliteit, inhoud en
      antwoordgedrag kunnen zonder voorafgaande aankondiging wijzigen. Uitkomsten kunnen in
      deze fase extra gevoelig zijn voor onjuistheden.
    </p>

    <H2>Artikel 4 — Geen advies</H2>
    <p>
      De informatie in de applicatie is algemeen en informatief van aard en vormt geen
      juridisch, medisch, financieel of arbeidsdeskundig advies. Voor advies over jouw
      persoonlijke situatie kun je terecht bij een vakbond, de ondernemingsraad, HR, een
      vertrouwenspersoon of een professioneel adviseur.
    </p>

    <H2>Artikel 5 — Eigen verantwoordelijkheid en geen rechten ontlenen</H2>
    <p>
      Je blijft zelf verantwoordelijk voor beslissingen die je neemt op basis van informatie
      uit de applicatie. Aan antwoorden, indicaties, berekeningen en overige uitkomsten kunnen
      geen rechten worden ontleend. De officiële bronnen — waaronder de cao, wet- en
      regelgeving en het functiehandboek — zijn altijd leidend; bij afwijkingen gaan de
      officiële bronnen voor.
    </p>

    <H2>Artikel 6 — Accountgebruik</H2>
    <p>
      Je account is persoonlijk en niet overdraagbaar. Je gaat zorgvuldig om met je
      inloggegevens en deelt deze niet met anderen. Je gebruikt de applicatie uitsluitend voor
      eigen, niet-commerciële doeleinden en voert geen gegevens in van anderen zonder hun
      medeweten. De beheerder kan een account deactiveren bij misbruik of na afloop van de
      testfase.
    </p>

    <H2>Artikel 7 — Beschikbaarheid</H2>
    <p>
      Er wordt gestreefd naar een goede beschikbaarheid van de applicatie, maar deze wordt
      niet gegarandeerd. De applicatie kan tijdelijk niet beschikbaar zijn, bijvoorbeeld door
      onderhoud, storingen of doorontwikkeling. Aan onderbrekingen kunnen geen rechten of
      aanspraken worden ontleend.
    </p>

    <H2>Artikel 8 — Fouten melden</H2>
    <p>
      Zie je een fout, een onjuist antwoord of ander onverwacht gedrag? Meld dit dan via de
      feedbackmogelijkheden in de applicatie of via {CONTACT}. Meldingen helpen om de kwaliteit
      van het systeem te verbeteren.
    </p>

    <H2>Artikel 9 — Aansprakelijkheid</H2>
    <p>
      Voor zover wettelijk toegestaan is iedere aansprakelijkheid voor schade die voortvloeit
      uit het gebruik van de applicatie — waaronder schade door onjuiste, onvolledige of
      verouderde informatie — uitgesloten. Niets in deze voorwaarden sluit aansprakelijkheid
      uit die op grond van dwingend recht niet kan worden uitgesloten of beperkt.
    </p>

    <H2>Artikel 10 — Wijzigingen</H2>
    <p>
      Deze voorwaarden kunnen worden gewijzigd, bijvoorbeeld bij doorontwikkeling van de
      applicatie of wijziging van wet- en regelgeving. Bij een nieuwe versie wordt bij het
      eerstvolgende gebruik opnieuw om akkoord gevraagd.
    </p>

    <H2>Artikel 11 — Toepasselijk recht</H2>
    <p>
      Op deze voorwaarden en het gebruik van de applicatie is Nederlands recht van toepassing.
    </p>
  </LegalShell>
);

export const Privacy = () => (
  <LegalShell title="Privacyverklaring" testId="doc-privacy">
    <p>
      Deze privacyverklaring beschrijft hoe Zoetwaren Kompas omgaat met jouw persoonsgegevens,
      in overeenstemming met de Algemene verordening gegevensbescherming (AVG). Zoetwaren
      Kompas is een kleinschalige testomgeving die wordt gebruikt om de werking, bruikbaarheid
      en kwaliteit van het systeem te evalueren.
    </p>

    <H2>1. Wie is verantwoordelijk?</H2>
    <p>
      Verwerkingsverantwoordelijke is {ENTITEIT}, gevestigd te {VESTIGING}. Voor vragen over
      privacy of het uitoefenen van je rechten kun je contact opnemen via {CONTACT}.
    </p>

    <H2>2. Welke gegevens verwerken we?</H2>
    <p>We verwerken de volgende categorieën gegevens:</p>
    <p>
      <strong>Accountgegevens:</strong> naam, e-mailadres, rol en een versleutelde
      (gehashte) versie van je wachtwoord. Je wachtwoord zelf wordt nooit leesbaar opgeslagen.<br />
      <strong>Gebruiksgegevens:</strong> de vragen die je stelt, de antwoorden die je
      ontvangt, uitgevoerde checks (zoals salaris-, functie- en tilcheck) en de feedback die
      je geeft.<br />
      <strong>Logginggegevens:</strong> technische logregels en auditlogs met tijdstippen,
      gebruikte bronnen en statusinformatie, bedoeld voor kwaliteitsbewaking en beveiliging.<br />
      <strong>Akkoord-registratie:</strong> datum, tijdstip en documentversies van jouw
      akkoord op de Algemene Voorwaarden en deze privacyverklaring.
    </p>

    <H2>3. Waarvoor gebruiken we je gegevens?</H2>
    <p>
      We gebruiken je gegevens voor: (a) het aanmaken en beveiligen van je account en het
      inloggen; (b) het beantwoorden van je vragen en het tonen van je eigen historie;
      (c) het evalueren en verbeteren van de kwaliteit van het systeem tijdens de testfase;
      (d) het aantoonbaar vastleggen van je akkoord. De grondslagen hiervoor zijn de
      uitvoering van de gebruiksafspraken, ons gerechtvaardigd belang bij een veilig en goed
      werkend systeem, en — waar van toepassing — jouw toestemming.
    </p>

    <H2>4. Vragen, antwoorden en AI-verwerking</H2>
    <p>
      Om je vraag te kunnen beantwoorden wordt de tekst van je vraag verwerkt door
      AI-taalmodellen van externe verwerkers. Voer daarom geen onnodige persoonsgegevens van
      jezelf of anderen in (zoals BSN, medische gegevens of gegevens van collega's). Zoetwaren
      Kompas neemt geen besluiten over jou met rechtsgevolgen op basis van geautomatiseerde
      verwerking; de applicatie geeft uitsluitend informatie en indicaties.
    </p>

    <H2>5. Bewaartermijnen</H2>
    <p>
      Accountgegevens bewaren we zolang je account actief is en uiterlijk tot het einde van de
      test-/pilotperiode, waarna ze worden verwijderd of geanonimiseerd. Gebruiks- en
      logginggegevens bewaren we niet langer dan nodig voor evaluatie en beveiliging, met een
      maximum van twaalf maanden na afloop van de testfase. De akkoord-registratie bewaren we
      zolang dat nodig is om het akkoord te kunnen aantonen.
    </p>

    <H2>6. Delen met derden</H2>
    <p>
      Je gegevens worden niet verkocht en niet gedeeld met derden voor commerciële doeleinden.
      Voor de werking van de applicatie wordt gebruikgemaakt van hostingdiensten en
      AI-verwerkers; met deze partijen gelden verwerkersafspraken. Gegevens van gebruikers
      blijven afgeschermd van andere gebruikers; beheerders zien uitsluitend wat nodig is voor
      beheer en kwaliteitsbewaking.
    </p>

    <H2>7. Beveiliging</H2>
    <p>
      We nemen passende technische en organisatorische maatregelen, waaronder versleutelde
      verbindingen (HTTPS), wachtwoord-hashing, beveiliging tegen inlogmisbruik,
      rolgebaseerde toegang en het loggen van beheerhandelingen.
    </p>

    <H2>8. Jouw rechten</H2>
    <p>
      Je hebt het recht op inzage, rectificatie, verwijdering en beperking van de verwerking
      van je gegevens, het recht om bezwaar te maken en het recht op overdraagbaarheid van je
      gegevens. Ook kun je een eerder gegeven toestemming intrekken. Neem hiervoor contact op
      via {CONTACT}. Ben je niet tevreden over de afhandeling, dan kun je een klacht indienen
      bij de Autoriteit Persoonsgegevens (autoriteitpersoonsgegevens.nl).
    </p>

    <H2>9. Wijzigingen</H2>
    <p>
      Deze privacyverklaring kan worden gewijzigd. Bij een nieuwe versie wordt bij het
      eerstvolgende gebruik opnieuw om akkoord gevraagd. De actuele versie is altijd
      beschikbaar in de applicatie.
    </p>
  </LegalShell>
);

export const Over = () => (
  <LegalShell title="Over Zoetwaren Kompas" testId="doc-over">
    <p>
      Zoetwaren Kompas is een kleinschalige testomgeving die wordt gebruikt om de werking,
      bruikbaarheid en kwaliteit van het systeem te evalueren. De applicatie helpt werknemers
      en OR-leden in de zoetwarensector op weg met vragen over arbeidsvoorwaarden — met de
      cao, wetgeving en het functiehandboek als basis.
    </p>

    <H2>Wat kun je ermee?</H2>
    <p>
      <strong>Vraag &amp; antwoord:</strong> stel je vraag in gewone taal; je ziet bij elk
      antwoord de status en kunt via "Waar staat dat?" nagaan waarop het antwoord is
      gebaseerd.<br />
      <strong>Salarischeck:</strong> inzicht in loon, toeslagen en vergoedingen.<br />
      <strong>Functiecheck:</strong> een indicatie van je functie-indeling op basis van je
      werkzaamheden.<br />
      <strong>Tilcheck:</strong> een eerste beoordeling van fysieke belasting bij tilwerk.
    </p>

    <H2>Hoe werkt het? (AI-disclaimer)</H2>
    <p>
      Zoetwaren Kompas maakt gebruik van kunstmatige intelligentie (AI). Antwoorden worden
      automatisch samengesteld op basis van brondocumenten zoals de Cao Zoetwaren, wetgeving
      en het functiehandboek. Daarbij gelden bewuste veiligheidsregels: de applicatie trekt
      geen juridische of medische conclusies, verzint geen rechten of plichten en verwijst bij
      twijfel door naar een vakbond, de ondernemingsraad, HR of een vertrouwenspersoon.
    </p>
    <p>
      Toch kan AI fouten maken. Antwoorden kunnen onjuist, onvolledig of verouderd zijn, ook
      wanneer ze overtuigend klinken. Het systeem bevindt zich bovendien in een test- en
      ontwikkelfase. Gebruik de antwoorden daarom als hulpmiddel en startpunt — niet als
      definitief oordeel. Aan de antwoorden kunnen geen rechten worden ontleend; de officiële
      bronnen zijn altijd leidend.
    </p>
    <div className="bg-[#FBF3E3] border border-[#EBD9B8] rounded-lg p-4 flex gap-3 text-sm text-[#6b4c1e]" data-testid="over-disclaimer-blok">
      <Info className="w-5 h-5 shrink-0 mt-0.5" />
      <span>{KORTE_DISCLAIMER}</span>
    </div>

    <H2>Fouten of feedback?</H2>
    <p>
      Zie je een fout of klopt een antwoord niet? Meld het via de feedbackmogelijkheden in de
      applicatie of via {CONTACT}. Jouw melding helpt het systeem te verbeteren.
    </p>

    <H2>Juridische informatie</H2>
    <p>
      Lees ook de <a href="/voorwaarden" className="underline">Algemene Voorwaarden</a> en de{" "}
      <a href="/privacy" className="underline">Privacyverklaring</a> (versie {DOC_VERSION}).
    </p>
  </LegalShell>
);
